import React, { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { createSearchParams, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useAppSelector } from "../Store"
import { useUser } from "../Services"
import { setStates } from "../Store/StateHandlers/active-slice"
import { hardNavigateToLogin, Pages, NavigationFunction as RootNavigation } from "../Navigation/root"
import { StyleObject } from "styletron-react"
import { logos, frames, glyphs } from "../Styles"
import { NewSelect } from "../Components"
import { ButtonTypes, SelectFieldOption_Values, NewFieldStyles } from "../Definitions"
import NewButton from "./BaseComponents/newButton"
import { clearAnimals } from "../Services/Agrisys_GqlDataCaller/AnimalDataCalls/AnimalDataSlices/animalsSlice"
import { clearActiveAnimals } from "../Store/activeStateHandlers/activeAnimalSlice"
import { clearFeedCurves } from "../Services/Agrisys_GqlDataCaller/FeedDataCalls/FeedDataSlices/feedCurvesSlice"
import { clearActiveLocations } from "../Store/activeStateHandlers/activeSectionSlice"

interface LogoDisplay_Props {
  ChooseSite?: boolean
  goBackBtn?: string,
  overrides?:{
    gridArea?: string
    borderRadius?: string
  }
  selectedPage?: string
  focus_view?: boolean
}

export default function LogoDisplay({goBackBtn, overrides, ChooseSite, selectedPage, focus_view}:LogoDisplay_Props) {
  const {t} = useTranslation()
  const dispatch = useDispatch()
  const { userFarms, getUserFarms, getFarmSettings, doLogOut } = useUser()
  const activeFarm = useAppSelector<string|undefined>(({activeData}) => activeData?.farm)
  const navigator = useNavigate()
 
  useEffect(() => {
    getUserFarms()
  },[getUserFarms])

  useEffect(() => {
    if (activeFarm && localStorage.getItem('activedomain')) {
      getFarmSettings(activeFarm)
    }
    if (activeFarm && !localStorage.getItem("activedomain")) {
      localStorage.setItem("activedomain", activeFarm);
    }
  },[activeFarm, getFarmSettings]) 

  const _Root = useMemo<StyleObject>(() => {
    return Object.assign(
      {} as StyleObject,
      overrides && overrides.gridArea ? { gridArea: overrides.gridArea } : {},
      overrides && overrides.borderRadius ? { borderRadius: overrides.borderRadius } : { borderRadius: "10px 0px 0px 0px" }
    )
  },[overrides])

  const Data = useMemo<{ label: string; domain: string; icon: JSX.Element }[]>(() => {
    const focusOption =
      selectedPage !== "op_operations"
        ? {
            label: t("loginFlow.def.switchTofocus_view", { defaultValue: "Switch to Focus View" }),
            domain: "navigate-to-focus_view",
            icon: <glyphs.focus_view size={focus_view ? "25px" : "20px"} />,
          }
        : {
            label: t("loginFlow.def.switchToFullView", { defaultValue: "Switch to Full View" }),
            domain: "navigate-to-fullView",
            icon: <glyphs.FullView size={focus_view ? "25px" : "20px"} />,
          }
  
    const siteListOption = {
      label: t("loginFlow.def.goToSiteList", { defaultValue: "Go to Site List" }),
      domain: "navigate-to-list",
      icon: <glyphs.SiteList size={focus_view ? "25px" : "20px"} />,
    }
  
    const logoutOption = {
      label: t("loginFlow.login.logOut", { defaultValue: "Log Out" }),
      domain: "logout",
      icon: <glyphs.Logout size={focus_view ? "25px" : "20px"} />,
    }
  
    // If userFarms not loaded, just show site list + logout
    if (!userFarms) {
      return [focusOption, siteListOption, logoutOption]
    }
  
    // Map user farms to options
    const farmOptions = userFarms.map(farm => ({
      label: farm.name,
      domain: farm.domain,
      icon: <></>,
    }))
  
    // Order: focus → site list → logout → farms
    return [focusOption, siteListOption, logoutOption, ...farmOptions]
  }, [focus_view, selectedPage, t, userFarms])
  

  const handleChange = (selectedOption: SelectFieldOption_Values | null) => {
    if (selectedOption === null) {
      return;
    }
    
  
    if (selectedOption.domain === 'navigate-to-list') {
      // dispatch(setStates({ type: 'farm', farm: undefined }));
      // RootNavigation({ Navigator: navigator, navigation_page: Pages.login,  });
      hardNavigateToLogin()
    } else if (selectedOption.domain === 'navigate-to-focus_view') {
      RootNavigation({ Navigator: navigator, navigation_page: Pages.op_operations, navigation_tab: undefined, replace: true, navArgs: { selected: selectedPage }, currentDomain: activeFarm });
    } else if (selectedOption.domain === 'navigate-to-fullView') {
      RootNavigation({ Navigator: navigator, navigation_page: Pages.kpi, navigation_tab: undefined, replace: true, navArgs: { selected: selectedPage }, currentDomain: activeFarm});
    } else if (selectedOption.domain === 'logout') {
      doLogOut();
      const tempLink = document.createElement('a');
      tempLink.href = window.location.origin;
      
      tempLink.rel = 'noreferrer';
      
      tempLink.click()
      // RootNavigation({ Navigator: navigator, navigation_page: Pages.def});
    } else {
      const selectedFarm = userFarms?.find((farm) => farm.domain === selectedOption.domain);
      if (selectedFarm && selectedFarm.legacy) {
        // RootNavigation({ Navigator: navigator, navigation_page: Pages.login, navigation_tab: undefined, replace: true, navArgs: { selected: selectedPage }});
        hardNavigateToLogin()
      }
      dispatch(setStates({ type: 'farm', farm: selectedOption.domain }));
      if (activeFarm !== selectedOption.domain) {
        dispatch(clearAnimals());
        dispatch(clearActiveAnimals());
        dispatch(clearFeedCurves());
        dispatch(clearActiveLocations()); 
      }
      if (selectedFarm?.domain) {
        window.location.href = window.location.origin+window.location.pathname+"?"+createSearchParams({domain: selectedFarm.domain}).toString();
      }
    }
  };
  

  return(
    <frames.LogoDisplay $focusView={focus_view} $style={_Root}>
      <img src={logos.freedaSolutions} style={{width: "275px", marginBottom: "15px"}} alt="Company logo"/>
      {/* Go Back Button on Logo Display */}
      {!goBackBtn?(<></>):(
        <NewButton
          content={goBackBtn}
          startEnhancer={glyphs.Arrow({rotate:180})}
          buttonType={ButtonTypes.textButton}
          onClick={() => {
            // RootNavigation({ Navigator: navigator, navigation_page: Pages.login, currentDomain: activeFarm})
            hardNavigateToLogin()
          }}
        />
      )}
      {/* SelectField to choose site but also go to site list in loginflow */}
      {!ChooseSite?(<></>):
        <NewSelect
          isSearchable
          options={Data}
          onChange = {(e) => handleChange(e ?? null)}
          fieldOverrides={{
            fieldStyle: NewFieldStyles.greyDarkSecondary,
            focus_view: focus_view ? true : false,
            menuWidth: focus_view ? "100%" : "280px",
            spliceBorder: 3
          }}
          valueOverrides={{
            defaultValue: Data.find(e => e.domain === activeFarm)
          }}
        />
      }
    </frames.LogoDisplay>  
  )
}